import { useEffect, useRef } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { useSmartNodeUpdates } from '@/hooks/useSmartNodeUpdates';
import { IFlowNode, IPlayNodeForm } from '@/types/plays';
import { useDnDAutoSave, useDnDGraph, useDnDUI } from '../contexts/DnDContext';

interface NodeFormSyncContext {
  values: Partial<IPlayNodeForm>;
  name?: string;
  form: UseFormReturn<IPlayNodeForm>;
}

interface NodeFormSyncOptions {
  actualConnectionId?: number;
  match: (name?: string) => boolean;
  skipIfPristine?: boolean;
  buildLocalDetails?: (
    context: NodeFormSyncContext,
    existingDetails?: Record<string, unknown>,
  ) => Record<string, unknown> | null;
  buildPatchDetails?: (context: NodeFormSyncContext) => Record<string, unknown> | null;
  mergeNestedKeys?: string[];
  mergeLocalDetails?: boolean;
  onChange?: (context: NodeFormSyncContext) => void;
  onAfterLocalUpdate?: () => void;
}

const mergeNested = (
  base: Record<string, unknown>,
  update: Record<string, unknown>,
  nestedKeys: string[],
) => {
  const next = { ...base, ...update };

  nestedKeys.forEach((key) => {
    const baseValue = base[key];
    const updateValue = update[key];
    if (
      baseValue &&
      updateValue &&
      typeof baseValue === 'object' &&
      typeof updateValue === 'object'
    ) {
      next[key] = {
        ...(baseValue as Record<string, unknown>),
        ...(updateValue as Record<string, unknown>),
      };
    }
  });

  return next;
};

export const useNodeFormSync = ({
  actualConnectionId: rawConnectionId,
  match,
  skipIfPristine = true,
  buildLocalDetails,
  buildPatchDetails,
  mergeNestedKeys = ['configuration'],
  mergeLocalDetails = true,
  onChange,
  onAfterLocalUpdate,
}: NodeFormSyncOptions) => {
  // Runtime guard: only accept real numeric database IDs for backend PATCH calls.
  // Client-side UUIDs (instanceId/reactFlowNodeId) passed as actualConnectionId
  // must not trigger API calls for unattached nodes.
  const actualConnectionId = (typeof rawConnectionId === 'number' && rawConnectionId > 0)
    ? rawConnectionId
    : undefined;
  const shouldLog =
    typeof window !== 'undefined' ? ((window as any).__DEBUG_DND__ ?? true) : true;
  const debugLog = (...args: unknown[]) => {
    if (!shouldLog) return;
    console.log('[NodeFormSync]', ...args);
  };

  // OPTIMIZATION: Batch multiple setNodes calls into one
  // This prevents multiple rapid form.watch triggers from causing 4+ setNodes calls
  const pendingLocalUpdateRef = useRef<{
    nodeId: string;
    details: Record<string, unknown>;
    scheduled: boolean;
  } | null>(null);

  // OPTIMIZATION: Batch mode flag - when true, form.watch callback skips processing
  // This prevents N form.watch invocations when calling N setValue() calls in a row
  // The withBatch helper sets this flag, allowing consumers to batch multiple setValue calls
  const batchModeRef = useRef(false);
  const batchedCallbackRef = useRef<(() => void) | null>(null);

  // OPTIMIZATION: Prevent duplicate subscriptions (React StrictMode can cause double-mounting)
  const subscriptionActiveRef = useRef(false);
  const form = useFormContext<IPlayNodeForm>();
  const { selectedNode, workflowId } = useDnDUI();
  const { setAutoSaved } = useDnDAutoSave();
  const { setNodes } = useDnDGraph();

  const { queueCustomActionDetailsUpdate } = useSmartNodeUpdates(actualConnectionId, workflowId, {
    onSuccess: () => setAutoSaved(true),
  });

  const lastPatchHashRef = useRef<string>('');

  // CRITICAL FIX: Store ALL values in refs to prevent infinite re-renders
  // The useEffect should only re-run when actualConnectionId changes (a primitive)
  // Everything else is accessed via refs to avoid dependency instability

  // Refs for callback functions
  const matchRef = useRef(match);
  const buildLocalDetailsRef = useRef(buildLocalDetails);
  const buildPatchDetailsRef = useRef(buildPatchDetails);
  const onChangeRef = useRef(onChange);
  const onAfterLocalUpdateRef = useRef(onAfterLocalUpdate);
  const skipIfPristineRef = useRef(skipIfPristine);
  const mergeNestedKeysRef = useRef(mergeNestedKeys);
  const mergeLocalDetailsRef = useRef(mergeLocalDetails);
  const queueCustomActionDetailsUpdateRef = useRef(queueCustomActionDetailsUpdate);

  // CRITICAL: Also store form and setNodes in refs
  // form from useFormContext and setNodes from useDnDGraph should be stable,
  // but storing in refs guarantees the useEffect won't re-run due to reference changes
  const formRef = useRef(form);
  const setNodesRef = useRef(setNodes);

  // Update ALL refs on each render (this doesn't trigger effect re-runs)
  matchRef.current = match;
  buildLocalDetailsRef.current = buildLocalDetails;
  buildPatchDetailsRef.current = buildPatchDetails;
  onChangeRef.current = onChange;
  onAfterLocalUpdateRef.current = onAfterLocalUpdate;
  skipIfPristineRef.current = skipIfPristine;
  mergeNestedKeysRef.current = mergeNestedKeys;
  mergeLocalDetailsRef.current = mergeLocalDetails;
  queueCustomActionDetailsUpdateRef.current = queueCustomActionDetailsUpdate;
  formRef.current = form;
  setNodesRef.current = setNodes;

  // Store selectedNode in a ref to avoid it triggering effect re-runs
  const selectedNodeRef = useRef(selectedNode);
  selectedNodeRef.current = selectedNode;
  const isDirtyRef = useRef(form.formState.isDirty);

  useEffect(() => {
    isDirtyRef.current = form.formState.isDirty;
  }, [form.formState.isDirty]);

  useEffect(() => {
    // OPTIMIZATION: Prevent duplicate subscriptions (React StrictMode double-mounts)
    if (subscriptionActiveRef.current) {
      debugLog('Subscription already active, skipping duplicate setup', { actualConnectionId });
      return;
    }
    subscriptionActiveRef.current = true;
    debugLog('Setting up form.watch subscription', { actualConnectionId });

    // CRITICAL: Use formRef.current to access form - this prevents useEffect re-runs
    const currentForm = formRef.current;

    const subscription = currentForm.watch((values, { name }) => {
      // OPTIMIZATION: Skip processing entirely when in batch mode
      // The batch callback will trigger a single update after all setValue calls complete
      if (batchModeRef.current) {
        debugLog('form.watch - SKIPPED (batch mode)', { name });
        return;
      }

      debugLog('form.watch triggered', { name, isDirty: isDirtyRef.current, skipIfPristine: skipIfPristineRef.current });

      // Use refs to get latest callback without including in dependencies
      if (!matchRef.current?.(name)) {
        debugLog('form.watch - name did not match, skipping', { name });
        return;
      }

      // CRITICAL FIX: Determine if we should skip based on pristine state
      // We now separate the logic: local updates always run, backend patches respect skipIfPristine
      //
      // ROOT-CAUSE HARDENING (#1736): read the dirty state LIVE here instead of
      // relying on isDirtyRef. isDirtyRef is updated in a useEffect (post-render),
      // but form.watch fires synchronously during onChange — so on the FIRST edit
      // of a pristine form isDirtyRef is still false, causing the patch to be
      // silently skipped (the #1736 bug). formRef.current.formState.isDirty is
      // computed synchronously by react-hook-form from current vs default values,
      // so it is accurate at callback time. isDirtyRef is kept as a safe fallback.
      const isDirtyNow = formRef.current?.formState?.isDirty ?? isDirtyRef.current;
      const shouldSkipPatch = skipIfPristineRef.current && !isDirtyNow;
      if (shouldSkipPatch) {
        debugLog('Will skip patch (pristine), but local updates will still run', { name });
      }

      debugLog('form.watch - PROCESSING', { name });

      // Create context using ref to get latest form
      const context: NodeFormSyncContext = {
        form: formRef.current,
        values: values as Partial<IPlayNodeForm>,
        name,
      };
      onChangeRef.current?.(context);

      // CRITICAL FIX: Always run buildLocalDetails to update node data for badge display
      // This ensures the UI reflects changes immediately, even for unsaved nodes
      const selectedNodeMeta = selectedNodeRef.current as
        | { reactFlowNodeId?: string; custom_action_details?: Record<string, unknown> }
        | undefined;
      if (buildLocalDetailsRef.current && selectedNodeMeta?.reactFlowNodeId) {
        const existingDetails = selectedNodeMeta.custom_action_details;
        const localDetails = buildLocalDetailsRef.current(context, existingDetails);
        debugLog('buildLocalDetails returned', { hasLocalDetails: !!localDetails, nodeId: selectedNodeMeta.reactFlowNodeId });
        if (localDetails) {
          // OPTIMIZATION: Batch multiple rapid updates into a single setNodes call
          // Instead of calling setNodes immediately for each form.watch trigger,
          // accumulate changes and flush once via a single microtask
          const nodeId = selectedNodeMeta.reactFlowNodeId;
          const pending = pendingLocalUpdateRef.current;

          if (pending && pending.nodeId === nodeId) {
            // Merge with existing pending update
            pending.details = mergeLocalDetailsRef.current
              ? mergeNested(pending.details, localDetails, mergeNestedKeysRef.current)
              : { ...pending.details, ...localDetails };
            debugLog('Merged with pending update', { nodeId });
          } else {
            // Start new pending update
            pendingLocalUpdateRef.current = {
              nodeId,
              details: localDetails,
              scheduled: false,
            };
          }

          // Schedule flush if not already scheduled
          if (!pendingLocalUpdateRef.current?.scheduled) {
            pendingLocalUpdateRef.current!.scheduled = true;
            debugLog('Scheduling batched setNodes via queueMicrotask');
            queueMicrotask(() => {
              const update = pendingLocalUpdateRef.current;
              if (!update) return;

              debugLog('setNodes microtask executing (batched)', { nodeId: update.nodeId });
              pendingLocalUpdateRef.current = null; // Clear before setNodes to allow new batches

              // OPTIMIZATION: Track if any node actually changed to avoid unnecessary re-renders
              let hasChanges = false;

              setNodesRef.current((prevNodes) => {
                const nextNodes = prevNodes.map((node) => {
                  if (node.id !== update.nodeId) return node;

                  const nodeExistingDetails =
                    (node.data?.custom_action_details as Record<string, unknown>) || {};
                  const mergedDetails = mergeLocalDetailsRef.current
                    ? mergeNested(nodeExistingDetails, update.details, mergeNestedKeysRef.current)
                    : update.details;

                  // OPTIMIZATION: Compare details to avoid creating new object if nothing changed
                  // This prevents unnecessary re-renders when form.watch triggers but values are same
                  const existingJson = JSON.stringify(nodeExistingDetails);
                  const mergedJson = JSON.stringify(mergedDetails);
                  if (existingJson === mergedJson) {
                    debugLog('setNodes skipped - no changes', { nodeId: update.nodeId });
                    return node; // Return same reference to prevent re-render
                  }

                  hasChanges = true;
                  debugLog('setNodes applying changes', { nodeId: update.nodeId, changedKeys: Object.keys(update.details) });

                  return {
                    ...node,
                    data: {
                      ...node.data,
                      custom_action_details: mergedDetails,
                    },
                  } as IFlowNode;
                });

                // If no changes, return the same array reference to prevent re-renders
                return hasChanges ? nextNodes : prevNodes;
              });

              if (hasChanges) {
                onAfterLocalUpdateRef.current?.();
              }
            });
          }
        }
      }

      // CRITICAL FIX: Only skip backend patch when pristine, not local updates
      // Backend patches are expensive (API calls), local updates are cheap (state changes)
      if (shouldSkipPatch) {
        debugLog('Skip patch (pristine)', { name });
        return;
      }

      if (buildPatchDetailsRef.current && actualConnectionId) {
        const patchDetails = buildPatchDetailsRef.current(context);
        if (patchDetails && Object.keys(patchDetails).length > 0) {
          const nextHash = JSON.stringify(patchDetails);
          if (nextHash !== lastPatchHashRef.current) {
            lastPatchHashRef.current = nextHash;
            debugLog('Queue patch update', { name, patchDetails });
            queueCustomActionDetailsUpdateRef.current(patchDetails);
          }
        }
      }
    });

    return () => {
      debugLog('Cleaning up form.watch subscription', { actualConnectionId });
      subscriptionActiveRef.current = false;
      subscription.unsubscribe();
    };
    // CRITICAL FIX: Only depend on actualConnectionId (a primitive)
    // All other values are accessed via refs to prevent infinite re-render loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualConnectionId]);

  // OPTIMIZATION: Helper to batch multiple setValue calls into a single form.watch processing
  // Usage: withBatch(() => { setValue(...); setValue(...); setValue(...); });
  // This prevents N form.watch invocations when calling N setValue() calls
  const withBatch = (callback: () => void) => {
    debugLog('withBatch START');
    batchModeRef.current = true;

    try {
      // Execute all setValue calls - form.watch will skip processing due to batchModeRef
      callback();
    } finally {
      batchModeRef.current = false;
      debugLog('withBatch END - triggering single update');

      // Manually trigger a single processing of the current form state
      // This replaces the N skipped form.watch calls with a single update
      const values = formRef.current.getValues();
      const context: NodeFormSyncContext = {
        form: formRef.current,
        values: values as Partial<IPlayNodeForm>,
        name: undefined, // Batch update - no specific field name
      };
      onChangeRef.current?.(context);

      // Update local node details
      const selectedNodeMeta = selectedNodeRef.current as
        | { reactFlowNodeId?: string; custom_action_details?: Record<string, unknown> }
        | undefined;
      if (buildLocalDetailsRef.current && selectedNodeMeta?.reactFlowNodeId) {
        const existingDetails = selectedNodeMeta.custom_action_details;
        const localDetails = buildLocalDetailsRef.current(context, existingDetails);
        if (localDetails) {
          const nodeId = selectedNodeMeta.reactFlowNodeId;
          let hasChanges = false;

          setNodesRef.current((prevNodes) => {
            const nextNodes = prevNodes.map((node) => {
              if (node.id !== nodeId) return node;
              const nodeExistingDetails =
                (node.data?.custom_action_details as Record<string, unknown>) || {};
              const mergedDetails = mergeLocalDetailsRef.current
                ? mergeNested(nodeExistingDetails, localDetails, mergeNestedKeysRef.current)
                : localDetails;

              // OPTIMIZATION: Compare details to avoid creating new object if nothing changed
              const existingJson = JSON.stringify(nodeExistingDetails);
              const mergedJson = JSON.stringify(mergedDetails);
              if (existingJson === mergedJson) {
                debugLog('withBatch setNodes skipped - no changes', { nodeId });
                return node;
              }

              hasChanges = true;
              return {
                ...node,
                data: {
                  ...node.data,
                  custom_action_details: mergedDetails,
                },
              } as IFlowNode;
            });

            return hasChanges ? nextNodes : prevNodes;
          });

          if (hasChanges) {
            onAfterLocalUpdateRef.current?.();
          }
        }
      }

      // Queue backend patch if applicable
      if (buildPatchDetailsRef.current && actualConnectionId) {
        const patchDetails = buildPatchDetailsRef.current(context);
        if (patchDetails && Object.keys(patchDetails).length > 0) {
          const nextHash = JSON.stringify(patchDetails);
          if (nextHash !== lastPatchHashRef.current) {
            lastPatchHashRef.current = nextHash;
            debugLog('withBatch - queue patch update', { patchDetails });
            queueCustomActionDetailsUpdateRef.current(patchDetails);
          }
        }
      }
    }
  };

  return {
    form,
    queueCustomActionDetailsUpdate,
    withBatch,
  };
};
